/**
 * 쉬는시간 지각 벌점 설정
 *
 * 등원 지각(하루 1회)과 별개로, 교시 시작보다 늦게 착석하면 교시별로 부과한다.
 * 설정은 operational_rules.rules->'late_return' 에 저장 (벌점 설정 테이블과 별개).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../../config/api';

interface LateReturnRule {
  enabled: boolean;
  grace_minutes: number;
  points: number;
  daily_cap: number;
  notify: boolean;
}

const DEFAULTS: LateReturnRule = {
  enabled: true,
  grace_minutes: 1,
  points: 1,
  daily_cap: 5,
  notify: false,
};

const getToken = () =>
  localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken') || '';

const LateReturnSettings: React.FC = () => {
  const [form, setForm] = useState<LateReturnRule>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/rules`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data?.late_return) setForm({ ...DEFAULTS, ...data.late_return });
      }
    } catch (e) {
      console.error('쉬는시간 지각 설정 조회 실패', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await fetch(`${API_BASE_URL}/api/v1/rules/late-return`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({ detail: '저장 실패' }));
        throw new Error(e.detail || `HTTP ${res.status}`);
      }
      alert('쉬는시간 지각 설정이 저장되었습니다.');
    } catch (e) {
      alert(`저장 실패: ${e instanceof Error ? e.message : e}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div className="pt-4 border-t">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-medium">쉬는시간 지각 벌점</div>
          <div className="text-sm text-gray-500">
            등원 지각과 <strong>별개</strong>입니다. 쉬는시간 끝나고 교시 시작보다 늦게
            들어오면 <strong>교시마다</strong> 부과됩니다.
            <br />
            정규 시간대만 적용되며(자율학습 구간 제외), 알림은 보내지 않습니다.
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer shrink-0">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            style={{ width: '1.15rem', height: '1.15rem' }}
            className="rounded accent-blue-600"
          />
          <span className="text-sm text-gray-700">{form.enabled ? '사용 중' : '사용 안 함'}</span>
        </label>
      </div>

      {form.enabled && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-700">유예 시간</span>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min={0}
                  max={120}
                  value={form.grace_minutes}
                  onChange={(e) =>
                    setForm({ ...form, grace_minutes: Math.max(0, parseInt(e.target.value) || 0) })
                  }
                  className="w-20 border rounded-lg px-3 py-2 text-center"
                />
                <span className="text-gray-500 text-sm">분 초과부터</span>
              </div>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-700">교시당 점수</span>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={form.points}
                  onChange={(e) =>
                    setForm({ ...form, points: Math.max(0, parseInt(e.target.value) || 0) })
                  }
                  className="w-20 border rounded-lg px-3 py-2 text-center"
                />
                <span className="text-gray-500 text-sm">점</span>
              </div>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-700">하루 상한</span>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min={0}
                  max={50}
                  value={form.daily_cap}
                  onChange={(e) =>
                    setForm({ ...form, daily_cap: Math.max(0, parseInt(e.target.value) || 0) })
                  }
                  className="w-20 border rounded-lg px-3 py-2 text-center"
                />
                <span className="text-gray-500 text-sm">건 (0 = 제한 없음)</span>
              </div>
            </label>
          </div>

          <p className="mt-2 text-xs text-gray-500">
            ※ 등원 지각으로 이미 벌점이 붙은 교시는 중복 부과하지 않습니다.
          </p>
        </>
      )}

      <button
        onClick={handleSave}
        disabled={saving}
        className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
      >
        {saving ? '저장 중…' : '쉬는시간 지각 설정 저장'}
      </button>
    </div>
  );
};

export default LateReturnSettings;
